import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { supabase } from '../../context/AuthContext';
import { convertToMelbourneDate } from '../../utils/dateUtils';

function useInViewOnce(options = {}) {
  const elementRef = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const node = elementRef.current;

    if (!node || isInView) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.35,
        rootMargin: '0px 0px -8% 0px',
        ...options,
      },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [isInView, options]);

  return [elementRef, isInView];
}

function ChartCard({ title, children }) {
  const [cardRef, isInView] = useInViewOnce();

  return (
    <div ref={cardRef} className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/60">
      <h4 className="mb-4 text-base font-semibold text-slate-900">{title}</h4>
      {children(isInView)}
    </div>
  );
}

function ChartSkeleton() {
  return (
    <div className="space-y-3">
      <div className="h-4 w-2/3 animate-pulse rounded bg-slate-200" />
      <div className="h-2 animate-pulse rounded bg-slate-100" />
      <div className="h-2 w-5/6 animate-pulse rounded bg-slate-100" />
      <div className="h-2 w-4/6 animate-pulse rounded bg-slate-100" />
    </div>
  );
}

function useAnimatedProgress(targetValue, shouldAnimate, duration = 900, startDelay = 0) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let frameId;
    let timeoutId;
    const safeTarget = Number.isFinite(targetValue) ? Math.max(targetValue, 0) : 0;

    if (!shouldAnimate) {
      setProgress(0);
      return undefined;
    }

    const startAnimation = () => {
      const startTime = performance.now();

      const tick = (now) => {
        const elapsed = now - startTime;
        const ratio = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - ratio, 3);
        setProgress(safeTarget * eased);

        if (ratio < 1) {
          frameId = requestAnimationFrame(tick);
        }
      };

      frameId = requestAnimationFrame(tick);
    };

    setProgress(0);
    timeoutId = window.setTimeout(startAnimation, startDelay);

    return () => {
      window.clearTimeout(timeoutId);
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [duration, shouldAnimate, startDelay, targetValue]);

  return progress;
}

function useAnimationReplay(shouldAnimate) {
  const [replayKey, setReplayKey] = useState(0);

  useEffect(() => {
    if (!shouldAnimate) {
      return undefined;
    }

    const replay = () => setReplayKey((current) => current + 1);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        replay();
      }
    };

    window.addEventListener('focus', replay);
    window.addEventListener('pageshow', replay);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', replay);
      window.removeEventListener('pageshow', replay);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [shouldAnimate]);

  return replayKey;
}

function AnimatedBar({ value, maxValue, colorClass, delay = 0, shouldAnimate }) {
  const targetWidth = maxValue > 0 ? Math.max((value / maxValue) * 100, value > 0 ? 6 : 0) : 0;
  const animatedWidth = useAnimatedProgress(targetWidth, shouldAnimate, 850, delay);

  return (
    <div className="h-2 overflow-hidden rounded-full bg-gray-100">
      <div
        className={`h-2 rounded-full ${colorClass} shadow-[0_0_18px_rgba(37,99,235,0.18)] transition-[filter] duration-300`}
        style={{ width: `${animatedWidth}%` }}
      />
    </div>
  );
}

function SimpleListChart({ items, emptyLabel, valuePrefix = '', shouldAnimate }) {
  if (!items.length) {
    return (
      <div className="rounded-[1.25rem] border border-dashed border-slate-200 bg-slate-50 px-4 py-5 shadow-sm">
        <p className="text-sm font-medium text-slate-700">{emptyLabel}</p>
        <p className="mt-1 text-xs text-slate-500">This usually means there is no matching data yet.</p>
      </div>
    );
  }

  const maxValue = Math.max(...items.map((item) => item.value), 1);

  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div key={item.label}>
          <div className="mb-1 flex items-center justify-between gap-3 text-sm text-gray-700">
            <span className="truncate">{item.label}</span>
            <span className="font-medium text-gray-900">
              {item.displayValue || `${valuePrefix}${item.value.toLocaleString(undefined, {
                maximumFractionDigits: valuePrefix ? 2 : 0,
                minimumFractionDigits: valuePrefix ? 2 : 0,
              })}`}
            </span>
          </div>
          <AnimatedBar value={item.value} maxValue={maxValue} colorClass="bg-blue-500" delay={index * 90} shouldAnimate={shouldAnimate} />
        </div>
      ))}
    </div>
  );
}

function DonutLegend({ items, total }) {
  return (
    <div className="space-y-3">
      {items.map((item) => {
        const percentage = total ? ((item.value / total) * 100).toFixed(1) : '0.0';
        return (
          <div key={item.label} className="flex items-center justify-between rounded-[1rem] border border-slate-200/80 bg-white/90 px-4 py-3 text-sm shadow-sm shadow-slate-200/50">
            <div className="flex items-center gap-3 text-gray-700">
              <span className="h-3.5 w-3.5 rounded-full ring-4 ring-white shadow-[0_0_0_1px_rgba(148,163,184,0.18)]" style={{ backgroundColor: item.color }} />
              <div>
                <p className="font-medium text-slate-800">{item.label}</p>
                <p className="text-xs text-slate-500">{percentage}% of total orders</p>
              </div>
            </div>
            <span className="text-base font-semibold text-gray-900">{item.value.toLocaleString('en-AU')}</span>
          </div>
        );
      })}
    </div>
  );
}

function DonutChart({ items, emptyLabel, shouldAnimate }) {
  const total = items.reduce((sum, item) => sum + item.value, 0);
  const replayKey = useAnimationReplay(shouldAnimate);
  const animatedSweep = useAnimatedProgress(100, shouldAnimate, 1550, 180 + replayKey * 0);
  const paidItem = items.find((item) => item.label === 'Paid') || items[0];
  const unpaidItem = items.find((item) => item.label === 'Unpaid') || items[1];
  const paidRatio = total ? paidItem.value / total : 0;
  const paidSweep = paidRatio * animatedSweep;
  const unpaidSweep = Math.max(animatedSweep - paidSweep, 0);
  const radius = 68;
  const strokeWidth = 18;
  const circumference = 2 * Math.PI * radius;
  const paidLength = (paidSweep / 100) * circumference;
  const unpaidLength = (unpaidSweep / 100) * circumference;
  const trackColor = '#dbe4f0';
  const paidColor = paidItem?.color || 'currentColor';
  const unpaidColor = unpaidItem?.color || '#93c5fd';

  if (!total) {
    return (
      <div className="rounded-[1.25rem] border border-dashed border-slate-200 bg-slate-50 px-4 py-5 shadow-sm">
        <p className="text-sm font-medium text-slate-700">{emptyLabel}</p>
        <p className="mt-1 text-xs text-slate-500">The chart will appear once paid and unpaid records are available.</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fbff_100%)] p-4 shadow-[0_18px_40px_rgba(15,23,42,0.05)]">
      <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr] xl:items-center">
        <div className="rounded-2xl border border-slate-100 bg-[radial-gradient(circle_at_top,#f8fbff_0%,#ffffff_62%)] px-4 pb-4 pt-5 text-blue-600 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
          <div className="mx-auto max-w-[320px]">
            <div className="relative mx-auto aspect-square w-full max-w-[208px] sm:max-w-[224px] lg:max-w-[232px] xl:max-w-[248px]">
              <svg viewBox="0 0 200 200" className="h-full w-full -rotate-90 overflow-visible" aria-hidden="true">
                <defs>
                  <filter id="donutGlow" x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="8" stdDeviation="10" floodColor="#0f172a" floodOpacity="0.08" />
                  </filter>
                </defs>
                <circle cx="100" cy="100" r={radius} fill="none" stroke={trackColor} strokeWidth={strokeWidth} />
                <circle
                  cx="100"
                  cy="100"
                  r={radius}
                  fill="none"
                  stroke={paidColor}
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  strokeDasharray={`${paidLength} ${circumference}`}
                  filter="url(#donutGlow)"
                />
                <circle
                  cx="100"
                  cy="100"
                  r={radius}
                  fill="none"
                  stroke={unpaidColor}
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  strokeDasharray={`${unpaidLength} ${circumference}`}
                  strokeDashoffset={-paidLength}
                  filter="url(#donutGlow)"
                />
              </svg>
              <div className="absolute inset-0 m-auto flex h-[102px] w-[102px] flex-col items-center justify-center rounded-full border border-slate-100 bg-white/95 text-center shadow-[0_12px_30px_rgba(15,23,42,0.08)] backdrop-blur-sm sm:h-[108px] sm:w-[108px] lg:h-[112px] lg:w-[112px] xl:h-[120px] xl:w-[120px]">
                <span className="text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-slate-400">Total</span>
                <span className="mt-1 text-[1.7rem] font-semibold leading-none text-slate-900 sm:text-[1.8rem] lg:text-[1.9rem] xl:text-[2rem]">{total.toLocaleString('en-AU')}</span>
              </div>
            </div>
          </div>
        </div>
        <DonutLegend items={items} total={total} />
      </div>
    </div>
  );
}

function LineBars({ items, colorClass, emptyLabel, shouldAnimate }) {
  if (!items.length) {
    return (
      <div className="rounded-[1.25rem] border border-dashed border-slate-200 bg-slate-50 px-4 py-5 shadow-sm">
        <p className="text-sm font-medium text-slate-700">{emptyLabel}</p>
        <p className="mt-1 text-xs text-slate-500">No trend data was returned for this range yet.</p>
      </div>
    );
  }

  const maxValue = Math.max(...items.map((item) => item.value), 1);

  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div key={item.label} className="grid grid-cols-[110px_1fr_70px] items-center gap-3 text-sm">
          <span className="text-gray-700">{item.label}</span>
          <AnimatedBar value={item.value} maxValue={maxValue} colorClass={colorClass} delay={index * 100} shouldAnimate={shouldAnimate} />
          <span className="text-right font-medium text-gray-900">{item.value}</span>
        </div>
      ))}
    </div>
  );
}

function formatCurrencyTick(value) {
  return Number(value || 0).toLocaleString('en-AU', {
    maximumFractionDigits: 0,
  });
}

function MultiLineChart({ items, series, emptyLabel, valuePrefix = '', shouldAnimate }) {
  const hasData = items.some((item) => series.some((entry) => (item[entry.key] || 0) > 0));
  const animatedItems = useMemo(
    () => items.map((item, index) => ({ ...item, animationBegin: index * 70 })),
    [items],
  );

  if (!hasData) {
    return (
      <div className="rounded-[1.25rem] border border-dashed border-slate-200 bg-slate-50 px-4 py-5 shadow-sm">
        <p className="text-sm font-medium text-slate-700">{emptyLabel}</p>
        <p className="mt-1 text-xs text-slate-500">The chart will appear once monthly revenue data is available.</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-[#f8faf8] p-4 sm:p-5">
      <div className="h-[320px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={animatedItems} margin={{ top: 12, right: 18, left: 8, bottom: 8 }}>
            <CartesianGrid stroke="#d1d5db" strokeDasharray="4 4" vertical />
            <XAxis
              dataKey="label"
              tick={{ fill: '#475569', fontSize: 12 }}
              tickLine={false}
              axisLine={{ stroke: '#94a3b8' }}
            />
            <YAxis
              tickFormatter={formatCurrencyTick}
              tick={{ fill: '#475569', fontSize: 12 }}
              tickLine={false}
              axisLine={{ stroke: '#94a3b8' }}
              width={64}
            />
            <Tooltip
              formatter={(value) => `${valuePrefix}${Number(value || 0).toLocaleString('en-AU', {
                minimumFractionDigits: valuePrefix ? 2 : 0,
                maximumFractionDigits: valuePrefix ? 2 : 0,
              })}`}
              labelStyle={{ color: '#0f172a', fontWeight: 600 }}
              contentStyle={{
                borderRadius: '14px',
                border: '1px solid #cbd5e1',
                boxShadow: '0 12px 30px rgba(15, 23, 42, 0.08)',
              }}
            />
            <Legend
              verticalAlign="bottom"
              align="center"
              iconType="circle"
              wrapperStyle={{ paddingTop: 18, color: '#334155', fontSize: '14px' }}
            />
            {series.map((entry) => (
              <Line
                key={entry.key}
                type="monotone"
                dataKey={entry.key}
                name={entry.label}
                stroke={entry.color}
                strokeWidth={4}
                isAnimationActive={shouldAnimate}
                animationDuration={1400}
                animationEasing="ease-out"
                dot={{ r: 4, strokeWidth: 3, fill: '#f8faf8' }}
                activeDot={{ r: 6, strokeWidth: 0 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function getMelbourneDate(date) {
  const melDate = convertToMelbourneDate(date);
  const [year, month, day] = melDate.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function formatDayLabel(date) {
  return date.toLocaleDateString('en-AU', { weekday: 'short' });
}

function formatMonthLabel(date) {
  return date.toLocaleDateString('en-AU', { month: 'short' });
}

function getWeekStart(date) {
  const weekStart = new Date(date);
  const day = weekStart.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  weekStart.setDate(weekStart.getDate() + diff);
  return weekStart;
}

function formatDateRangeLabel(startDate, endDate) {
  const startLabel = startDate.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
  const endLabel = endDate.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
  return `${startLabel} - ${endLabel}`;
}

function getRowValue(row) {
  return Number(row.transaction_price) || 0;
}

function getModelLabel(row) {
  const parts = [row.car_make, row.car_model].filter(Boolean);
  return parts.length ? parts.join(' ') : 'Unknown Model';
}

function getVehicleTypeLabel(row) {
  return row.vehicle_type || 'Unknown Vehicle Type';
}

const VEHICLE_TYPE_ORDER = [
  'Bikes',
  'Campervan',
  'Common',
  'Fixed Price',
  'Trucks',
  'Unique',
  'Welcab Ramp',
  'Welcab Side Seat',
];

const STATE_ORDER = [
  'ACT',
  'NSW',
  'NT',
  'QLD',
  'SA',
  'TAS',
  'VIC',
  'WA',
];

function getCustomerLabel(row) {
  return row.user_name || 'Unknown Customer';
}

function getStateLabel(row) {
  return row.state || 'Unknown State';
}

function formatStatePerformanceItems(stateMap) {
  const totalOrders = Array.from(stateMap.values()).reduce((sum, value) => sum + value, 0);

  return Array.from(stateMap, ([label, value]) => ({
    label,
    value,
    displayValue: `${value} (${totalOrders ? ((value / totalOrders) * 100).toFixed(1) : '0.0'}%)`,
  })).sort((a, b) => b.value - a.value);
}

const GraphAnalysis = () => {
  const [loading, setLoading] = useState(true);
  const currentYear = new Date().getFullYear();
  const previousYear = currentYear - 1;
  const [comparisonMode, setComparisonMode] = useState('revenue');
  const [chartData, setChartData] = useState({
    dayAnalysisTitle: 'Day Analysis',
    dayAnalysis: [],
    weeklyGrowth: [],
    monthlyGrowth: [],
    yearlyAnalysis: [],
    last2YearsComparisonCount: [],
    last2YearsComparisonRevenue: [],
    topModelReports: [],
    vehicleTypePerformance: [],
    statePerformance: [],
    ordersPaidUnpaid: [],
    topCustomers: [],
  });

  useEffect(() => {
    let isActive = true;

    const fetchCharts = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('admin_submissions_view')
        .select('created_at, status, transaction_price, state, car_make, car_model, vehicle_type, user_name, user_id')
        .eq('is_archived', false);

      if (error || !data || !isActive) {
        if (isActive) {
          setLoading(false);
        }
        return;
      }

      const now = new Date();
      const melNow = getMelbourneDate(now);
      const targetYear = 2026;
      const currentWeekStart = getWeekStart(melNow);
      const currentWeekEnd = new Date(currentWeekStart);
      currentWeekEnd.setDate(currentWeekStart.getDate() + 6);

      const dayAnalysisMap = new Map();
      const weeklyGrowthMap = new Map();
      const monthlyGrowthMap = new Map();
      const yearlyAnalysisMap = new Map();
      const last2YearsCountMap = new Map();
      const last2YearsRevenueMap = new Map();
      const modelReportMap = new Map();
      const vehicleTypeMap = new Map(VEHICLE_TYPE_ORDER.map((label) => [label, 0]));
      const stateMap = new Map(STATE_ORDER.map((label) => [label, 0]));
      const customerMap = new Map();
      const paidUnpaidMap = new Map([
        ['Paid', 0],
        ['Unpaid', 0],
      ]);

      for (let index = 0; index < 7; index += 1) {
        const date = new Date(currentWeekStart);
        date.setDate(currentWeekStart.getDate() + index);
        dayAnalysisMap.set(formatDayLabel(date), 0);
      }

      for (let index = 4; index >= 0; index -= 1) {
        const weekStart = new Date(melNow);
        weekStart.setDate(melNow.getDate() - (melNow.getDay() === 0 ? 6 : melNow.getDay() - 1) - index * 7);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        weeklyGrowthMap.set(formatDateRangeLabel(weekStart, weekEnd), 0);
      }

      for (let index = 0; index < 12; index += 1) {
        const monthDate = new Date(targetYear, index, 1);
        monthlyGrowthMap.set(formatMonthLabel(monthDate), 0);
      }

      for (let index = 2; index >= 0; index -= 1) {
        yearlyAnalysisMap.set(String(melNow.getFullYear() - index), 0);
      }

      const currentYear = melNow.getFullYear();
      const previousYear = currentYear - 1;

      for (let index = 0; index < 12; index += 1) {
        const monthLabel = formatMonthLabel(new Date(currentYear, index, 1));
        last2YearsCountMap.set(monthLabel, {
          [String(previousYear)]: 0,
          [String(currentYear)]: 0,
        });
        last2YearsRevenueMap.set(monthLabel, {
          [String(previousYear)]: 0,
          [String(currentYear)]: 0,
        });
      }

      data.forEach((row) => {
        const createdDate = getMelbourneDate(new Date(row.created_at));
        const isInCurrentWeek = createdDate >= currentWeekStart && createdDate <= currentWeekEnd;
        const dayLabel = formatDayLabel(createdDate);
        if (isInCurrentWeek && dayAnalysisMap.has(dayLabel)) {
          dayAnalysisMap.set(dayLabel, dayAnalysisMap.get(dayLabel) + 1);
        }

        const monday = getWeekStart(createdDate);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        const weekLabel = formatDateRangeLabel(monday, sunday);
        if (weeklyGrowthMap.has(weekLabel)) {
          weeklyGrowthMap.set(weekLabel, weeklyGrowthMap.get(weekLabel) + 1);
        }

        const monthLabel = formatMonthLabel(createdDate);
        if (createdDate.getFullYear() === targetYear && monthlyGrowthMap.has(monthLabel)) {
          monthlyGrowthMap.set(monthLabel, monthlyGrowthMap.get(monthLabel) + 1);
        }

        const yearLabel = String(createdDate.getFullYear());
        if (yearlyAnalysisMap.has(yearLabel)) {
          yearlyAnalysisMap.set(yearLabel, yearlyAnalysisMap.get(yearLabel) + 1);
        }
        if (createdDate.getFullYear() === previousYear || createdDate.getFullYear() === currentYear) {
          const comparisonMonthLabel = formatMonthLabel(createdDate);
          const monthCount = last2YearsCountMap.get(comparisonMonthLabel);
          if (monthCount) {
            monthCount[yearLabel] += 1;
          }

          if (row.status === 'Paid') {
            const monthRevenue = last2YearsRevenueMap.get(comparisonMonthLabel);
            if (monthRevenue) {
              monthRevenue[yearLabel] += getRowValue(row);
            }
          }
        }

        if (row.status === 'Paid') {
          paidUnpaidMap.set('Paid', paidUnpaidMap.get('Paid') + 1);
        } else {
          paidUnpaidMap.set('Unpaid', paidUnpaidMap.get('Unpaid') + 1);
        }

        if (createdDate.getFullYear() === targetYear) {
          const modelLabel = getModelLabel(row);
          modelReportMap.set(modelLabel, (modelReportMap.get(modelLabel) || 0) + 1);

          const vehicleTypeLabel = getVehicleTypeLabel(row);
          vehicleTypeMap.set(vehicleTypeLabel, (vehicleTypeMap.get(vehicleTypeLabel) || 0) + 1);

          const stateLabel = getStateLabel(row);
          stateMap.set(stateLabel, (stateMap.get(stateLabel) || 0) + 1);

          if (row.status === 'Paid') {
            const customerKey = row.user_id || `unknown-${getCustomerLabel(row)}`;
            const currentCustomer = customerMap.get(customerKey) || {
              label: getCustomerLabel(row),
              value: 0,
            };
            customerMap.set(customerKey, {
              label: currentCustomer.label,
              value: currentCustomer.value + getRowValue(row),
            });
          }
        }

      });

      if (!isActive) {
        return;
      }

      setChartData({
        dayAnalysisTitle: `Day Analysis (${formatDateRangeLabel(currentWeekStart, currentWeekEnd)})`,
        dayAnalysis: Array.from(dayAnalysisMap, ([label, value]) => ({ label, value })),
        weeklyGrowth: Array.from(weeklyGrowthMap, ([label, value]) => ({ label, value })),
        monthlyGrowth: Array.from(monthlyGrowthMap, ([label, value]) => ({ label, value })),
        yearlyAnalysis: Array.from(yearlyAnalysisMap, ([label, value]) => ({ label, value })),
        last2YearsComparisonCount: Array.from(last2YearsCountMap, ([label, value]) => ({
          label,
          [String(previousYear)]: value[String(previousYear)],
          [String(currentYear)]: value[String(currentYear)],
        })),
        last2YearsComparisonRevenue: Array.from(last2YearsRevenueMap, ([label, value]) => ({
          label,
          [String(previousYear)]: value[String(previousYear)],
          [String(currentYear)]: value[String(currentYear)],
        })),
        topModelReports: Array.from(modelReportMap, ([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value).slice(0, 5),
        vehicleTypePerformance: Array.from(vehicleTypeMap, ([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value),
        statePerformance: formatStatePerformanceItems(stateMap),
        topCustomers: Array.from(customerMap.values()).sort((a, b) => b.value - a.value).slice(0, 5),
        ordersPaidUnpaid: [
          { label: 'Paid', value: paidUnpaidMap.get('Paid'), color: '#2563eb' },
          { label: 'Unpaid', value: paidUnpaidMap.get('Unpaid'), color: '#93c5fd' },
        ],
      });
      setLoading(false);
    };

    fetchCharts();

    return () => {
      isActive = false;
    };
  }, []);

  return (
    <div className="min-h-[300px] rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm shadow-slate-200/60 sm:p-8">
      {loading && (
        <div className="mb-8 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4">
          <div className="flex items-center gap-3 text-sm font-medium text-slate-600">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
            Loading performance overview...
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/50">
                <ChartSkeleton />
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="mb-12">
        <h3 className="text-md font-medium mb-4 flex items-center uppercase tracking-[0.08em]">
          <svg className="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
          Time Analysis
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 ">
          <ChartCard title="Top Selling Model Reports of 2026">
            {(isInView) => <SimpleListChart items={chartData.topModelReports} emptyLabel="No model report data available." shouldAnimate={isInView} />}
          </ChartCard>
          <ChartCard title="Vehicle Type Performance in Year 2026">
            {(isInView) => <SimpleListChart items={chartData.vehicleTypePerformance} emptyLabel="No vehicle type data available." shouldAnimate={isInView} />}
          </ChartCard>
          <ChartCard title="Top customers by spend">
            {(isInView) => <SimpleListChart items={chartData.topCustomers} emptyLabel="No customer data available." valuePrefix="$" shouldAnimate={isInView} />}
          </ChartCard>
          <ChartCard title="State Performance in Year 2026">
            {(isInView) => <SimpleListChart items={chartData.statePerformance} emptyLabel="No state data available." shouldAnimate={isInView} />}
          </ChartCard>
          <ChartCard title={chartData.dayAnalysisTitle}>
            {(isInView) => <LineBars items={chartData.dayAnalysis} colorClass="bg-sky-500" emptyLabel="No daily data available." shouldAnimate={isInView} />}
          </ChartCard>
          <ChartCard title="Weekly Analysis">
            {(isInView) => <LineBars items={chartData.weeklyGrowth} colorClass="bg-indigo-500" emptyLabel="No weekly data available." shouldAnimate={isInView} />}
          </ChartCard>
          <ChartCard title="Monthly Analysis">
            {(isInView) => <LineBars items={chartData.monthlyGrowth} colorClass="bg-violet-500" emptyLabel="No monthly data available." shouldAnimate={isInView} />}
          </ChartCard>
          <ChartCard title="Yearly Analysis">
            {(isInView) => <LineBars items={chartData.yearlyAnalysis} colorClass="bg-amber-500" emptyLabel="No yearly data available." shouldAnimate={isInView} />}
          </ChartCard>
          <ChartCard title="Last 2 Years Comparison">
            {(isInView) => (
              <>
                <div className="mb-4 inline-flex rounded-[1rem] border border-slate-200 bg-slate-50 p-1 shadow-sm">
                  <button
                    type="button"
                    onClick={() => setComparisonMode('count')}
                    className={`rounded-[0.8rem] px-3.5 py-2 text-sm font-medium transition-all duration-200 ${comparisonMode === 'count' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                  >
                    Order Count
                  </button>
                  <button
                    type="button"
                    onClick={() => setComparisonMode('revenue')}
                    className={`rounded-[0.8rem] px-3.5 py-2 text-sm font-medium transition-all duration-200 ${comparisonMode === 'revenue' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                  >
                    Revenue
                  </button>
                </div>
                <MultiLineChart
                  items={comparisonMode === 'count' ? chartData.last2YearsComparisonCount : chartData.last2YearsComparisonRevenue}
                  series={[
                    { key: String(previousYear), label: String(previousYear), color: '#0f766e' },
                    { key: String(currentYear), label: String(currentYear), color: '#2563eb' },
                  ]}
                  emptyLabel={comparisonMode === 'count' ? 'No two-year order count data available.' : 'No two-year revenue data available.'}
                  valuePrefix={comparisonMode === 'revenue' ? '$' : ''}
                  shouldAnimate={isInView}
                />
              </>
            )}
          </ChartCard>
        </div>
      </div>
      <hr className="mb-8 border-slate-200" />
      <div>
        <h3 className="text-md font-medium mb-4 flex items-center uppercase tracking-[0.08em]">
          <svg className="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
          Order & Customer Insights
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <ChartCard title="Orders Paid vs Unpaid">
            {(isInView) => <DonutChart items={chartData.ordersPaidUnpaid} emptyLabel="No payment status data available." shouldAnimate={isInView} />}
          </ChartCard>
        </div>
      </div>
    </div>
  );
};

export default GraphAnalysis;